"use client";

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DollarSign, CreditCard, Zap, Shield, Download, AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { showSuccess, showError } from '@/utils/toast';
import { StripeCheckout } from '@/components/StripeCheckout';

const AdminBilling = () => {
  const { profile, company } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const queryClient = useQueryClient();

  // Fetch subscription data
  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription', company?.id],
    queryFn: async () => {
      if (!company?.id) return null;
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('company_id', company.id)
        .single();
      
      if (error) {
        if (error.message.includes('permission')) {
          showError('Access denied. Contact superadmin.');
          return null;
        }
        throw error;
      }
      return data;
    },
    enabled: !!company?.id && profile?.role === 'admin',
    retry: (failureCount, error) => failureCount < 3 && !error.message.includes('permission'),
  });

  // Fetch invoices
  const { data: invoices } = useQuery({
    queryKey: ['invoices', company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        if (error.message.includes('permission')) {
          showError('Access denied. Contact superadmin.');
          return [];
        }
        throw error;
      }
      return data || [];
    },
    enabled: !!company?.id && profile?.role === 'admin',
    retry: (failureCount, error) => failureCount < 3 && !error.message.includes('permission'),
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!subscription?.stripe_subscription_id) throw new Error('No active subscription');
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { subscription_id: subscription.stripe_subscription_id },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to cancel');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      showSuccess('Subscription cancelled. You can upgrade anytime.');
    },
    onError: (error) => {
      showError(error.message || 'Failed to cancel subscription');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const plan = subscription?.plan || 'basic';
  const status = subscription?.status || 'inactive';
  const nextBilling = subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Billing & Subscriptions</h1>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setShowUpgrade(true)}>
            <Zap className="mr-2 h-4 w-4" />
            Upgrade Plan
          </Button>
          {status === 'active' && (
            <Button variant="destructive" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}>
              {cancelMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DollarSign className="mr-2 h-4 w-4" />}
              Cancel Subscription
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Current Plan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-medium">Plan</div>
              <Badge variant={plan === 'enterprise' ? 'default' : 'secondary'}>{plan}</Badge>
            </div>
            <div>
              <div className="font-medium">Status</div>
              <Badge variant={status === 'active' ? 'default' : 'secondary'}>{status}</Badge>
            </div>
            <div>
              <div className="font-medium">Next Billing</div>
              <div>{nextBilling || 'N/A'}</div>
            </div>
            <div>
              <div className="font-medium">Amount</div>
              <div>£{subscription?.prorated_amount ? (subscription.prorated_amount / 100).toFixed(2) : '0.00'}</div>
            </div>
          </div>
          {status === 'past_due' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Your subscription is past due. Please update payment method.</AlertDescription>
            </Alert>
          )}
          {status === 'cancelled' && (
            <Alert variant="secondary">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Subscription cancelled. Upgrade to restore access.</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Invoices ({invoices?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {invoices?.slice(0, 5).map((invoice) => (
              <div key={invoice.id} className="flex justify-between items-center p-3 border rounded">
                <div>
                  <div className="font-medium">Invoice #{invoice.invoice_number}</div>
                  <div className="text-sm text-muted-foreground">£{invoice.total_amount.toFixed(2)}</div>
                </div>
                <div className="text-right">
                  <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>{invoice.status}</Badge>
                  <div className="text-xs text-muted-foreground">{new Date(invoice.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
            {invoices?.length === 0 && <p className="text-muted-foreground">No invoices yet.</p>}
          </div>
          <Button variant="outline" className="mt-4">
            <Download className="mr-2 h-4 w-4" />
            Download All Invoices
          </Button>
        </CardContent>
      </Card>

      {showUpgrade && (
        <Card>
          <CardHeader>
            <CardTitle>Upgrade Your Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <StripeCheckout onSuccess={() => setShowUpgrade(false)} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminBilling;